import { z } from "zod";
import { responseSchema } from "../http.js";
import { emailSchema, fullNameSchema } from "./auth.js";
/**
 * El formulario de contacto de la tienda: quién escribe, a qué correo se
 * le contesta y qué dice.
 *
 * ── Por qué reutiliza los esquemas del alta ──────────────────────────
 * `emailSchema` y `fullNameSchema` salen de `auth.ts` tal cual, sin copia.
 * El correo de quien escribe por el formulario es el MISMO correo que el
 * de quien se registra: mismo recorte, mismas minúsculas, mismo techo de
 * 254 caracteres y el mismo mensaje cuando está mal escrito. Volver a
 * declararlo aquí habría creado dos definiciones de "correo válido" que
 * empiezan iguales y se separan en el primer arreglo que solo toque una —
 * y entonces el mismo correo se aceptaría en una pantalla y no en la otra.
 *
 * ── Los tres techos ──────────────────────────────────────────────────
 * Ninguno de los tres campos es libre. Este endpoint es PÚBLICO y sin
 * sesión: un mensaje sin límite de tamaño es una invitación a llenar la
 * base con un solo POST, y el límite de 1 MB del cuerpo de Express llega
 * demasiado tarde —ya son un millón de caracteres por mensaje—.
 *   · nombre  →  80 (el de `fullNameSchema`)
 *   · correo  → 254 (el de `emailSchema`, que es el máximo de un correo)
 *   · mensaje → 2000, ver abajo
 */
/**
 * Dos mil caracteres: unas 350 palabras, más de lo que cabe en la caja sin
 * hacer scroll y bastante para cualquier pregunta real. Quien necesita más
 * está escribiendo un documento, y para eso está el correo directo que la
 * misma sección enseña al lado del formulario.
 */
export const CONTACT_MESSAGE_MAX_LENGTH = 2000;
/**
 * Diez caracteres de mínimo. No es una barrera contra nadie —un bot los
 * escribe—; es que "hola" o "?" no son un mensaje al que se pueda
 * contestar, y quien lo manda se queda esperando una respuesta que nadie
 * sabe redactar. El mensaje del error dice qué hacer, no qué falló.
 */
export const CONTACT_MESSAGE_MIN_LENGTH = 10;
export const contactMessageSchema = z
    .string()
    .trim()
    .min(1, "Escribe tu mensaje")
    .min(CONTACT_MESSAGE_MIN_LENGTH, "Cuéntanos un poco más para poder ayudarte")
    .max(CONTACT_MESSAGE_MAX_LENGTH, `Máximo ${CONTACT_MESSAGE_MAX_LENGTH} caracteres`);
/** Lo que viaja al API cuando alguien pulsa "Enviar mensaje". */
export const contactRequestSchema = z.object({
    /**
     * `fullNameSchema`: de 2 a 80 caracteres, letras con acentos, apóstrofes,
     * guiones y puntos. Sin dígitos — igual que en el alta.
     */
    name: fullNameSchema,
    email: emailSchema,
    message: contactMessageSchema,
});
/**
 * La respuesta dice `received`, no `sent`, y la diferencia es el encargo
 * entero.
 *
 * El servidor GUARDA el mensaje primero y manda el correo después. Lo que
 * puede prometer con certeza es que el mensaje quedó registrado; que el
 * correo de aviso haya salido depende de Resend, de la red y de que haya
 * un dominio verificado — tres cosas que hoy pueden fallar y que no deben
 * cambiar lo que se le dice a quien escribió.
 *
 * Por eso tampoco hay un campo `emailed` en la respuesta: sería un detalle
 * de la operación interna que a quien escribe no le sirve para nada, y
 * enseñárselo solo abriría la puerta a mensajes distintos según si el
 * proveedor de correo estaba de pie. El estado del envío se guarda en el
 * documento (`emailedAt` / `emailError`), que es donde sirve: para
 * reintentar.
 */
export const contactReceiptSchema = z.object({
    received: z.literal(true),
});
export const contactResponseSchema = responseSchema(contactReceiptSchema);
// ── Rutas ────────────────────────────────────────────────
export const CONTACT_ROUTES = {
    /** Público y sin sesión. Con rate limit por IP en el servidor. */
    send: "/api/contact",
};
// ── El buzón del panel ───────────────────────────────────
/**
 * Los mensajes guardados, vistos desde `admin/support` (Anahí, 27-ago):
 * quién escribió, qué, cuándo, si el aviso por correo salió, y si ya se
 * ATENDIÓ. `resolvedAt` es fecha y no booleano por la misma razón que
 * `emailedAt`: contesta también "¿cuánto tardamos en responder?".
 */
export const supportMessageSchema = z.object({
    id: z.string(),
    name: z.string(),
    email: z.string(),
    message: z.string(),
    createdAt: z.iso.datetime(),
    emailedAt: z.iso.datetime().optional(),
    resolvedAt: z.iso.datetime().optional(),
});
export const supportQuerySchema = z.object({
    page: z.coerce.number().int().min(1).default(1),
    perPage: z.coerce.number().int().min(1).max(100).default(25),
    status: z.enum(["open", "resolved"]).optional(),
});
export const supportListSchema = z.object({
    items: z.array(supportMessageSchema),
    total: z.number().int().min(0),
    page: z.number().int().min(1),
    perPage: z.number().int().min(1),
});
export const supportListResponseSchema = responseSchema(supportListSchema);
export const supportMessageResponseSchema = responseSchema(z.object({ message: supportMessageSchema }));
export const SUPPORT_ROUTES = {
    adminList: "/api/admin/support",
    adminOne: (id) => `/api/admin/support/${id}`,
};
//# sourceMappingURL=contact.js.map